import React, {  useContext, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { translations } from "./translations";
import { FarmContext } from "../../contexts/FarmContext";

const EventTooltip = ({ data, setData, dialogState, setDialogState, setAffectedColumns }) => {
    const { t, i18n } = useTranslation();
    const [_, dispatch] = useContext(FarmContext);
    const paletasFenologia = dispatch({ type: "get", path: "paletasFenologia" });

    const mapNames = useMemo(() => (translations(t)), [t]);

    const maxCharacters = 100;

    let affectedColumns = [];
    // loop all properties in data_json
    for (const key of Object.keys(data.data_json)){
        if (key === "fertilizante" || key === "nutriente_id") continue; // duplicated key
        let columnText = "";
        let valueText = "";
        // set label of property to be shown, if it has translation it will use that
        // otherwise will use generic db fieldname
        if (mapNames[key]) columnText+= `${mapNames[key]}`;
        else columnText+= `${key}`;

        // standarize oldValue and newValue
        // handling cases where this this values are objects or primitive values
        let oldValue = data.data_json[key]?.oldValue;
        let newValue = data.data_json[key]?.newValue;
        if (oldValue && typeof oldValue === "object"){
            if (key === "nutrientes_estanque"){
                oldValue = oldValue.map(nutriente => `${nutriente.nombre} (${nutriente.porcentaje}%)`).join("; ");
            }
            else if (key === "paleta_colores"){
                const idPalette = paletasFenologia?.find(p => {
                    return p.palette?.every((color, idx) => color === oldValue[idx]);
                })?.id || "-";
                oldValue = t("farms.fenologia.paletaN", { id: idPalette });
            }
            else if (key === "estados_fenologicos"){
                oldValue = oldValue.map(e => `${e.nombre} (${e.duracion} ${t("forms.dias")})`).join("; ");
            }
            else oldValue = oldValue?.nombre?.[i18n.language] || oldValue?.nombre?.["en"] || oldValue?.nombre || oldValue?.[i18n.language] || oldValue?.["en"] || oldValue?.ciudad;
        }

        if (newValue && typeof newValue === "object"){
            if (key === "nutrientes_estanque"){
                newValue = newValue.map(nutriente => `${nutriente.nombre} (${nutriente.porcentaje}%)`).join("; ");
            }
            else if (key === "tipousuario_api_permitido"){
                newValue = newValue.map(el => el[i18n.language]).join("; ");
            }
            else if (key === "paleta_colores"){
                const idPalette = paletasFenologia?.find(p => {
                    return p.palette?.every((color, idx) => color === newValue[idx]);
                })?.id || "-";
                newValue = t("farms.fenologia.paletaN", { id: idPalette });
            }
            else if (key === "estados_fenologicos"){
                newValue = newValue.map(e => `${e.nombre} (${e.duracion} ${t("forms.dias")})`).join("; ");
            }
            else newValue = newValue?.nombre?.[i18n.language] || newValue?.nombre?.["en"] || newValue?.nombre || newValue?.[i18n.language] || newValue?.["en"] || newValue?.ciudad;
        }

        // handle boolean parameters like 'esta_habilitado'
        if (typeof oldValue === "boolean") oldValue = oldValue ? t("general.answerYes") : t("general.answerNo");
        if (typeof newValue === "boolean") newValue = newValue ? t("general.answerYes") : t("general.answerNo");
        if (typeof data.data_json[key] === "boolean") data.data_json[key] = data.data_json[key] ? t("general.answerYes") : t("general.answerNo");

        if (key === "prefijo" && data.descripcion === "Farm Info Update"){
            oldValue = oldValue ? t(oldValue).split(" {")[0] : null;
            newValue = newValue ? t(newValue).split(" {")[0] : null;
        }

        if (oldValue && newValue && (oldValue !== newValue)) valueText+= `${t("systemEvents.from")} "${oldValue}" ${t("systemEvents.to")} "${newValue}"`;
        else if (oldValue && !newValue) valueText+= `${t("systemEvents.from")} "${oldValue}" ${t("systemEvents.to")} ${t("txt.Vacio_LABEL")}`;
        else if (!oldValue && newValue) valueText+= `"${newValue}"`;
        else if (data.data_json[key] && typeof data.data_json[key] !== "object") valueText+= `${data.data_json[key]}`;
        else if (data.data_json[key]?.nombre) valueText+= `${data.data_json[key]?.nombre?.[i18n.language] || data.data_json[key]?.nombre}`;

        // handle Map Info Update
        if (data.descripcion === "Map Info Update" && ["sectores", "nodos"].includes(key)){
            valueText+= data.data_json[key] ? Object.keys(data.data_json[key]).map(el => {
                if (data.data_json[key][el].changes) return `"${data.data_json[key][el].nombre}"`;
                return `"${data.data_json[key][el].nombre}" (${t("systemEvents.deleted")})`;
            }).join("; ") : "";
        }

        if (data.data_json[key] === "CERES" || valueText === "\"CERES\"") valueText+= ` (${t("external.alineador_img")})`;

        // sortidx will permit show properties with translation first
        if (valueText) affectedColumns =  [...affectedColumns, { columnText, valueText, sortIdx: mapNames[key] ? 0 : 1 }];
    }

    affectedColumns.sort((a, b) => a.sortIdx - b.sortIdx);

    // Truncate tooltip content according maxCharacters allowed
    let totalLength = 0;
    const truncatedCols = [];
    for (const { columnText, valueText } of affectedColumns){
        const col = `${columnText}: ${valueText}`;
        if (totalLength + col.length <= maxCharacters) {
            truncatedCols.push({ columnText, valueText });
            totalLength += col.length;
        } else {
            truncatedCols.push({ columnText, valueText: "...", isTruncated: true });
            break;
        }
    }
    return (
        <>
            <div style={{ maxHeight: "250px", overflowY: "auto" }}>
                {
                    truncatedCols.map((el, idx) => {
                        if (el.isTruncated) {
                            return <p key={idx}>{el.columnText}: {el.valueText}
                                <button type="button" className="btn btn-link click-txt"
                                    style={{ color: "white", fontSize: "12px"  }} onClick={() => {
                                        setData(data);
                                        setAffectedColumns(affectedColumns);
                                        setDialogState(true);}}
                                >
                                        ({t("systemEvents.seemore")})
                                </button>
                            </p>;
                        } else return <p key={idx}>{el.columnText}: {el.valueText}</p>;
                    })
                }
            </div>
        </>
    );
};

export default EventTooltip;
